/**
 * Integrytas Group — Common Security Module (CSM)
 * useAdaptiveList — RLS-correct adaptive list loader.
 *
 * The trap this solves: a screen that loads a whole table and filters
 * CLIENT-SIDE silently truncates once the row set the user can see exceeds
 * PostgREST's `max_rows` cap (config.toml [api], default 1000). Records vanish
 * with no error and the search box can't find them (see the apiDb truncation
 * guard in api_client.ts).
 *
 * The fix, in ONE request: fetch the first CAP rows AND the exact RLS-scoped
 * total (`count: 'exact'`), then adapt:
 *
 *   total <= CAP  → 'client' mode. The hook holds the full (facet-filtered) set,
 *                   so text search is applied CLIENT-SIDE — instant, no
 *                   per-keystroke round-trip. This keeps the snappy UX small
 *                   tables have today.
 *   total >  CAP  → 'server' mode. Text search is pushed to the DB (ilike over
 *                   `searchColumns`) so any row is reachable; only CAP rows load
 *                   per query, with a `truncated` flag for a "refine your search"
 *                   hint.
 *
 * Facets are ALWAYS applied server-side (cheap `.in()` filters). They also shrink
 * the counted set, so a narrow facet selection can flip a large table back into
 * 'client' mode automatically.
 *
 * The count is computed under RLS (it is the caller's own authorized total), so
 * the mode decision is correct per user — unlike a physical `pg_class.reltuples`
 * or `OFFSET`/overfetch probe, which ignore RLS and are defeated by `max_rows`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiDb } from './api_client';

export const ADAPTIVE_LIST_CAP = Number(import.meta.env.VITE_LIST_CAP ?? 1000);

export interface AdaptiveFacet {
  column: string;
  values: (string | number)[];
}

/** Minimal apiDb query surface exposed to the `filter` escape hatch. */
export interface AdaptiveQuery {
  eq(col: string, v: unknown): AdaptiveQuery;
  neq(col: string, v: unknown): AdaptiveQuery;
  in(col: string, v: unknown[]): AdaptiveQuery;
  is(col: string, v: unknown): AdaptiveQuery;
  gt(col: string, v: unknown): AdaptiveQuery;
  gte(col: string, v: unknown): AdaptiveQuery;
  lt(col: string, v: unknown): AdaptiveQuery;
  lte(col: string, v: unknown): AdaptiveQuery;
  like(col: string, v: unknown): AdaptiveQuery;
  ilike(col: string, v: unknown): AdaptiveQuery;
  or(expr: string): AdaptiveQuery;
}

export interface AdaptiveListParams<Raw, Row> {
  /** Base relation (table or view) to read. */
  table: string;
  /** PostgREST select string (may embed related resources). */
  columns: string;
  /** Sort applied server-side (and the stable order client mode filters within). */
  order: { column: string; ascending?: boolean };
  /** Current search term (raw; the hook debounces it internally). */
  search: string;
  /** Raw columns matched with ilike in server mode (and, by default, in client mode). */
  searchColumns: string[];
  /** Facet selections applied server-side as `.in(column, values)` (empty = ignored). */
  facets?: AdaptiveFacet[];
  /**
   * Escape hatch for server-side conditions the declarative `facets` cannot
   * express — null checks (`.is(col, null)`), ranges (`.gte`), raw `.or(...)`.
   * Receives the apiDb query builder and must return it. Applied in BOTH the
   * probe and the server-search query, so filtering stays correct in server mode.
   * Memoize it (useCallback) and include it in `deps` if it closes over state.
   */
  filter?: (q: AdaptiveQuery) => AdaptiveQuery;
  /** Map a raw row to the screen's row shape. */
  map: (raw: Raw) => Row;
  /**
   * Optional client-mode search accessor. When a screen searches over derived
   * fields (e.g. a `_tl` display_text or a resolved FK name) rather than the raw
   * `searchColumns`, return the searchable text for a raw row here. Defaults to
   * concatenating the raw `searchColumns` values.
   */
  searchText?: (raw: Raw) => string;
  cap?: number;
  debounceMs?: number;
  /** Extra reload triggers (e.g. active locale). */
  deps?: unknown[];
  /** Skip loading while false (e.g. waiting on a prerequisite). */
  enabled?: boolean;
}

export interface AdaptiveListResult<Row> {
  rows: Row[];
  /** Exact RLS-scoped total for the current facets (and search, in server mode). */
  total: number;
  mode: 'client' | 'server';
  /** Server mode and more rows match than were returned — show a refine hint. */
  truncated: boolean;
  busy: boolean;
  error: string | null;
  reload: () => void;
}

const sanitizeIlike = (term: string): string => term.replace(/[%,()]/g, ' ').trim();

export function useAdaptiveList<Raw = Record<string, unknown>, Row = Raw>(
  params: AdaptiveListParams<Raw, Row>,
): AdaptiveListResult<Row> {
  const {
    table, columns, order, search, searchColumns, facets, filter, map, searchText,
    cap = ADAPTIVE_LIST_CAP, debounceMs = 300, deps = [], enabled = true,
  } = params;

  const [baseRows, setBaseRows] = useState<Raw[]>([]);   // facet-filtered page (mode probe)
  const [serverRows, setServerRows] = useState<Raw[] | null>(null); // server-search results
  const [baseTotal, setBaseTotal] = useState(0);
  const [searchTotal, setSearchTotal] = useState(0);
  const [mode, setMode] = useState<'client' | 'server'>('client');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [debouncedSearch, setDebouncedSearch] = useState(search.trim());
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), debounceMs);
    return () => clearTimeout(id);
  }, [search, debounceMs]);

  // Stable dependency keys so effects don't re-run on referentially-new arrays.
  const facetKey = useMemo(
    () => JSON.stringify((facets ?? []).map(f => [f.column, [...f.values].map(String).sort()])),
    [facets],
  );
  const orderKey = `${order.column}:${order.ascending !== false}`;
  const searchColKey = searchColumns.join(',');
  const depsKey = JSON.stringify(deps);

  const applyFacets = <B extends { in: (c: string, v: unknown[]) => B }>(q: B): B => {
    for (const f of facets ?? []) if (f.values.length > 0) q = q.in(f.column, f.values as unknown[]);
    return q;
  };

  // Effect A — structural load: facet-filtered page + exact count → decide mode.
  // Runs on table/columns/order/facets/deps/reload changes (NOT on search).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setBusy(true); setError(null);
    void (async () => {
      try {
        let q = apiDb.from(table).select(columns, { count: 'exact' })
          .order(order.column, { ascending: order.ascending !== false });
        q = applyFacets(q as never) as never;
        if (filter) q = filter(q as unknown as AdaptiveQuery) as unknown as typeof q;
        const res = await q.limit(cap);
        if (cancelled) return;
        if (res.error) throw res.error;
        const data = (res.data ?? []) as Raw[];
        const total = res.count ?? data.length;
        setBaseRows(data);
        setBaseTotal(total);
        setMode(total > cap ? 'server' : 'client');
      } catch (e) {
        if (!cancelled) { setError((e as Error).message); setBaseRows([]); setBaseTotal(0); }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, columns, orderKey, facetKey, depsKey, cap, enabled, reloadToken]);

  // Effect B — server-mode search: push the term to the DB. No-op in client mode,
  // or when there are no server-searchable columns (search targets derived fields —
  // the screen filters the loaded window client-side instead).
  useEffect(() => {
    if (!enabled || mode !== 'server' || searchColumns.length === 0) { setServerRows(null); setSearchTotal(0); return; }
    if (!debouncedSearch) { setServerRows(null); setSearchTotal(0); return; }
    const term = sanitizeIlike(debouncedSearch);
    if (!term) { setServerRows(null); setSearchTotal(0); return; }
    let cancelled = false;
    setBusy(true); setError(null);
    void (async () => {
      try {
        let q = apiDb.from(table).select(columns, { count: 'exact' })
          .order(order.column, { ascending: order.ascending !== false });
        q = applyFacets(q as never) as never;
        if (filter) q = filter(q as unknown as AdaptiveQuery) as unknown as typeof q;
        q = q.or(searchColumns.map(c => `${c}.ilike.%${term}%`).join(',')) as never;
        const res = await q.limit(cap);
        if (cancelled) return;
        if (res.error) throw res.error;
        setServerRows((res.data ?? []) as Raw[]);
        setSearchTotal(res.count ?? ((res.data ?? []) as Raw[]).length);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, mode, table, columns, orderKey, facetKey, searchColKey, depsKey, cap, enabled]);

  const defaultSearchText = (raw: Raw): string =>
    searchColumns.map(c => String((raw as Record<string, unknown>)[c] ?? '')).join(' ');
  const getText = searchText ?? defaultSearchText;

  const result = useMemo(() => {
    if (mode === 'server') {
      const raw = debouncedSearch && serverRows !== null ? serverRows : baseRows;
      const total = debouncedSearch && serverRows !== null ? searchTotal : baseTotal;
      return { raw, total, truncated: total > raw.length };
    }
    // client mode: full facet-filtered set in hand — filter by search in-memory.
    const q = debouncedSearch.toLowerCase();
    const raw = q ? baseRows.filter(r => getText(r).toLowerCase().includes(q)) : baseRows;
    return { raw, total: baseTotal, truncated: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, baseRows, serverRows, baseTotal, searchTotal, debouncedSearch, searchColKey]);

  const rows = useMemo(() => result.raw.map(map), [result.raw, map]);

  const reloadRef = useRef(() => setReloadToken(t => t + 1));
  return {
    rows,
    total: result.total,
    mode,
    truncated: result.truncated,
    busy,
    error,
    reload: reloadRef.current,
  };
}
